import { Component, ViewChild, ElementRef, inject } from '@angular/core';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MAX_FILE_UPLOAD_SIZE_BYTES, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../shared/constants';

export interface FileUploadDialogData {
  projectId: string;
}

export interface FileUploadResult {
  success: boolean;
  file?: File;
  error?: string;
}

/** Dialog for uploading new plan PDF files to the project, with drag-and-drop support and upload progress tracking. */
@Component({
  selector: 'app-file-upload-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatProgressBarModule, MatSnackBarModule],
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss'],
})
export class FileUploadDialogComponent {
  private dialogRef = inject<MatDialogRef<FileUploadDialogComponent>>(MatDialogRef);
  data = inject<FileUploadDialogData>(MAT_DIALOG_DATA);
  private snackBar = inject(MatSnackBar);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  readonly maxFileSizeMb = MAX_FILE_UPLOAD_SIZE_MB;

  selectedFile: File | null = null;
  isDragOver = false;
  uploading = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    // Validate file type
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      this.snackBar.open('Please select a PDF file', 'Close', { duration: 3000 });
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
      this.snackBar.open(`File size must be less than ${MAX_FILE_UPLOAD_SIZE_MB}MB`, 'Close', { duration: 3000 });
      return;
    }

    this.selectedFile = file;
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  onUpload(): void {
    if (!this.selectedFile) {
      this.snackBar.open('Please select a file to upload', 'Close', { duration: 3000 });
      return;
    }

    // Close dialog and return the selected file for upload
    this.dialogRef.close({
      success: true,
      file: this.selectedFile,
    });
  }

  isUploadValid(): boolean {
    return this.selectedFile !== null && !this.uploading;
  }
}
